/**
 * Horizontal scroller: arrows, drag, and momentum.
 *
 * Shared by the product rails and the department tiles so the two behave
 * identically - a flick in one feels like a flick in the other. The caller owns
 * the markup and passes the wrapper; this only needs a `[data-cc-track]` inside
 * it, and will use `.cc-arrow--prev` / `.cc-arrow--next` if they are there.
 *
 * Returns `{ track, sync }` so a caller that replaces the track's contents can
 * re-run the arrow visibility check, or null if the markup is not what it needs.
 */
(() => {
  const FRICTION = 0.94; // per 60fps frame
  const MIN_V = 0.02; // px/ms below which the glide is over

  window.ccScroller = function ccScroller(wrap) {
    if (!wrap) return null;
    const track = wrap.querySelector('[data-cc-track]');
    if (!track) return null;
    const prev = wrap.querySelector('.cc-arrow--prev');
    const next = wrap.querySelector('.cc-arrow--next');

    const maxScroll = () => track.scrollWidth - track.clientWidth;

    const sync = () => {
      const max = maxScroll();
      const overflowing = max > 4;
      if (prev) prev.hidden = !overflowing || track.scrollLeft <= 2;
      if (next) next.hidden = !overflowing || track.scrollLeft >= max - 2;
    };

    // Native smooth scrolling is unreliable here (and a no-op in some engines),
    // so the slide is animated by hand - assigning scrollLeft always works.
    const reduce = matchMedia('(prefers-reduced-motion: reduce)');
    let anim = null;

    const stop = () => {
      if (anim) { cancelAnimationFrame(anim); anim = null; }
      track.classList.remove('is-sliding');
    };

    const page = (dir) => {
      const to = Math.max(0, Math.min(maxScroll(), track.scrollLeft + dir * track.clientWidth * 0.9));
      stop();
      if (reduce.matches) { track.scrollLeft = to; sync(); return; }
      const from = track.scrollLeft, dist = to - from, t0 = performance.now(), dur = 380;
      track.classList.add('is-sliding');
      const step = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        track.scrollLeft = from + dist * e;
        if (p < 1) { anim = requestAnimationFrame(step); }
        else { anim = null; track.classList.remove('is-sliding'); sync(); }
      };
      anim = requestAnimationFrame(step);
    };

    if (prev) prev.addEventListener('click', () => page(-1));
    if (next) next.addEventListener('click', () => page(1));
    track.addEventListener('scroll', sync, { passive: true });
    new ResizeObserver(sync).observe(track);
    sync();

    // Drag to scroll with momentum. Velocity is tracked in px/ms while dragging,
    // then the track keeps gliding on release and decelerates under friction, so
    // a faster flick throws it further. A drag must not fire the card's link, so
    // clicks are swallowed once the pointer has actually travelled.
    let down = false, moved = false, startX = 0, startLeft = 0;
    let lastX = 0, lastT = 0, vel = 0;

    const glide = () => {
      track.classList.add('is-sliding');
      let v = vel, prevT = performance.now();
      const step = (t) => {
        const dt = Math.min(32, t - prevT); prevT = t;
        track.scrollLeft += v * dt;
        v *= Math.pow(FRICTION, dt / 16.667);
        const x = track.scrollLeft;
        if (Math.abs(v) < MIN_V || x <= 0 || x >= maxScroll()) {
          anim = null; track.classList.remove('is-sliding'); sync(); return;
        }
        anim = requestAnimationFrame(step);
      };
      anim = requestAnimationFrame(step);
    };

    // Images and links are draggable by default on a desktop browser, so a
    // mousedown on a product photo starts the browser's own drag-and-drop and
    // swallows the pointermove events this needs - the rail simply would not
    // move when grabbed by the picture, only by the card around it. Touch never
    // hit this because there is no native drag gesture there.
    track.addEventListener('dragstart', (e) => e.preventDefault());

    track.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      stop(); // grabbing kills any glide
      down = true; moved = false; vel = 0;
      startX = e.clientX; startLeft = track.scrollLeft;
      lastX = e.clientX; lastT = performance.now();
    });

    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < 5) return;
      if (!moved) {
        moved = true;
        // Capture can throw for a pointer the element does not own; the
        // drag works without it, so never let it abort the handler.
        try { track.setPointerCapture(e.pointerId); } catch (_) {}
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - dx;
      const now = performance.now(), dt = now - lastT;
      if (dt > 0) {
        // Blended so one jittery sample cannot dominate the throw.
        vel = vel * 0.7 + ((lastX - e.clientX) / dt) * 0.3;
        lastX = e.clientX; lastT = now;
      }
      e.preventDefault();
    });

    const end = () => {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
      if (!moved) return;
      setTimeout(() => { moved = false; }, 0);
      // A pause before releasing means a deliberate stop, not a flick.
      if (performance.now() - lastT > 100) vel = 0;
      if (!reduce.matches && Math.abs(vel) > 0.05) glide(); else sync();
    };

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    return { track, sync };
  };
})();
